import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles, Tooltip } from "@material-ui/core";
import { InfoOutlined } from "@material-ui/icons";
import { DropzoneArea } from "material-ui-dropzone";
import { useHistory, useLocation } from "react-router-dom";
import { AuthService } from "../../api/AuthService";
import { LoadingIndicator } from "../../components/LoadingIndicator";
import { RBAppbar } from "../../components/RBAppbar";
import { RBAlert } from "../../components/RBAlert";
import { RBButton } from "../../components/RBButton";
import { RBContext } from "../../components/RBContext";
import { renderRBPayText } from "../../components/RBPayWordmark";
import { RoutePathConstant } from "../../util/Common";
import { TopAppBarHeight } from "../../theme/ComponentTheme";
import { RBPayDocumentPreviewModal } from "../rbPaySettings/components/RBPayDocumentPreviewModal";
import { RBPayStepProgress, RBPayStepNotice } from "../rbPaySettings/components/RBPayStepProgress";
import { RBPaySetupSection } from "../rbPaySettings/components/RBPaySetupSection";
import { RB_PAY_SETUP_COLORS } from "../rbPaySettings/constants/rbPaySettings.constants";
import { buildRBPaySetupTheme, RBPaySetupTheme } from "../rbPaySettings/utils/rbPaySettings.theme";
import { useRBPayrollOwnerSettings } from "./hooks/useRBPayrollOwnerSettings";
import { RBPayrollSetupSelection } from "./components/RBPayrollSetupSelection";
import {
  RB_PAYROLL_ABOUT_BULLETS,
  RB_PAYROLL_ABOUT_INTRO,
  RB_PAYROLL_ABOUT_TITLE,
  RB_PAYROLL_ALLOWED_DOCUMENT_TYPES,
  RB_PAYROLL_AUTHORIZATION_TEXT,
  RB_PAYROLL_AUTHORIZED_SUMMARY,
  RB_PAYROLL_COPY_SUBMIT_MESSAGE,
  RB_PAYROLL_COPY_SUBMIT_TITLE,
  RB_PAYROLL_DOCUMENT_HELPER_TEXT,
  RB_PAYROLL_EDIT_SUBMIT_MESSAGE,
  RB_PAYROLL_EDIT_SUBMIT_TITLE,
  RB_PAYROLL_FIRST_SUBMIT_MESSAGE,
  RB_PAYROLL_FIRST_SUBMIT_TITLE,
  RB_PAYROLL_MAX_DOCUMENT_SIZE,
  RB_PAYROLL_NO_CHANGES_TOOLTIP,
  RB_PAYROLL_SETUP_NOTES,
  RB_PAYROLL_STATEMENT_ACCESS_NOTE,
  RB_PAYROLL_STATUS_COPY,
  RB_PAYROLL_SUBMIT_ERROR_MESSAGE,
  RB_PAYROLL_SUBMIT_ERROR_TITLE,
} from "./constants/rbPayrollOwner.constants";
import {
  RBPayrollLocationCreationMode,
  RBPayrollLocationCreationModeValue,
  RBPayrollStatus,
} from "./types/rbPayrollOwner.types";
import { getRBPayrollStatusLabel, isImageDocument, maskAccountNumber } from "./utils/rbPayrollOwner.formatters";
import { AccountInfoContext } from "../../components/AccountInfoContext";

const C = RB_PAY_SETUP_COLORS;

const useStyles = makeStyles((theme) => ({
  root: { minHeight: "100vh", background: C.pageBg },
  content: {
    maxWidth: 840,
    margin: "0 auto",
    padding: "28px 32px 64px",
    [theme.breakpoints.down("xs")]: { padding: "20px 16px 48px" },
  },
  contentNarrow: {
    maxWidth: 760,
    margin: "0 auto",
    padding: "32px 28px 60px",
    [theme.breakpoints.down("xs")]: { padding: "20px 16px 48px" },
  },
  appbarActions: { display: "flex", alignItems: "center", gap: "16px" },
  appbarStatements: { display: "flex", alignItems: "center", gap: "6px" },
  statementsButton: {
    padding: "8px 18px",
    background: (p: RBPaySetupTheme) => p.primary,
    color: "#fff",
    border: "none",
    borderRadius: 7,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  infoIcon: { fontSize: 18, color: C.textMuted, cursor: "help" },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: 11,
    fontWeight: 700,
    borderRadius: 999,
    padding: "3px 10px",
    whiteSpace: "nowrap",
  },
  stickyChecklist: { position: "sticky", top: TopAppBarHeight, zIndex: 50, background: C.pageBg },
  banner: {
    background: (p: RBPaySetupTheme) => `linear-gradient(135deg, ${p.bannerFrom} 0%, ${p.bannerTo} 100%)`,
    borderRadius: 12,
    padding: "22px 28px",
    marginBottom: 18,
  },
  bannerHead: { display: "flex", alignItems: "center", gap: "10px", marginBottom: 10 },
  bannerIcon: {
    width: 30,
    height: 30,
    borderRadius: 7,
    background: "rgba(255,255,255,0.13)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  bannerTitle: { fontSize: 14.5, fontWeight: 700, color: "#fff", margin: 0 },
  bannerSubTitle: { fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.95)", margin: "0 0 8px" },
  bannerIntro: { fontSize: 12.5, lineHeight: 1.65, color: "rgba(255,255,255,0.82)", margin: "0 0 10px", whiteSpace: "pre-line" as const },
  bannerBullets: { display: "flex", flexDirection: "column", gap: "5px", marginBottom: 12 },
  bannerBulletRow: { display: "flex", alignItems: "flex-start", gap: "8px" },
  bannerBulletDot: {
    width: 4,
    height: 4,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.6)",
    flexShrink: 0,
    marginTop: 7,
  },
  bannerBulletText: { fontSize: 12, color: "rgba(255,255,255,0.8)", margin: 0, lineHeight: 1.55 },
  bannerFooter: { borderTop: "1px solid rgba(255,255,255,0.13)", paddingTop: 10 },
  bannerMeta: { fontSize: 12, color: "rgba(255,255,255,0.7)", margin: 0 },
  bannerLink: { color: "#fff", fontWeight: 500 },
  noticeBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    padding: "13px 16px",
    borderRadius: 8,
    marginBottom: 22,
  },
  noticeTitle: { fontSize: 13, fontWeight: 600, margin: "0 0 2px" },
  noticeText: { fontSize: 12.5, margin: 0, lineHeight: 1.5 },
  twoCol: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "18px",
    marginBottom: 16,
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
  },
  inputLabel: {
    display: "block",
    fontSize: 10.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: C.textLabel,
    marginBottom: 7,
  },
  input: {
    width: "100%",
    padding: "10px 14px",
    border: `1.5px solid ${C.optionOffBorder}`,
    borderRadius: 8,
    fontSize: 14,
    color: C.textDark,
    background: C.optionOffBg,
  },
  fieldError: { color: C.errorText, fontSize: 12.5, minHeight: 20, marginTop: 5 },
  uploadLabelRow: { marginBottom: 9 },
  dropzoneUploaded: {
    border: `1.5px dashed #CCC5E0`,
    borderRadius: 10,
    padding: "12px 14px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "12px",
    background: C.optionOffBg,
  },
  // material-ui-dropzone renders a tall centred box by default; these overrides
  // collapse it to a compact single row (small icon on the left, text on the
  // right) to match the RB Pay upload field. Behaviour is unchanged.
  dropzoneEmpty: {
    minHeight: "0 !important",
    padding: "12px 14px !important",
    borderRadius: "10px !important",
    "& .MuiDropzoneArea-textContainer": {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "flex-start",
      gap: "12px",
    },
    "& .MuiDropzoneArea-text": {
      order: 2,
      fontSize: 14,
      fontWeight: 600,
      color: C.textDark,
      margin: 0,
    },
    "& .MuiDropzoneArea-icon": {
      order: 1,
      width: 34,
      height: 34,
      color: "#9B8FC0",
      flexShrink: 0,
    },
  },
  dropzonePrimary: { fontSize: 14, fontWeight: 600, color: C.textDark, margin: 0 },
  filePreviewImg: { width: 44, height: 44, borderRadius: 8, objectFit: "cover", flexShrink: 0 },
  fileActions: { display: "flex", alignItems: "center", gap: "10px", marginTop: 10 },
  linkButton: {
    background: "transparent",
    border: "none",
    color: (p: RBPaySetupTheme) => p.secondary,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    padding: 0,
  },
  uploadHelp: { fontSize: 11.5, color: C.textFaint, margin: "7px 0 0" },
  summaryBox: { background: C.summaryBg, border: `1px solid ${C.summaryBorder}`, borderRadius: 9, overflow: "hidden" },
  summaryRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 16px", gap: "16px" },
  summaryKey: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: C.textLabel,
  },
  summaryValue: { fontSize: 13.5, fontWeight: 500, color: C.textDark, textAlign: "right", overflowWrap: "anywhere" },
  checkboxBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: "13px",
    padding: "16px 18px",
    background: C.noteBg,
    border: `1px solid ${C.noteBorder}`,
    borderRadius: 9,
  },
  checkbox: { width: 16, height: 16, flexShrink: 0, marginTop: 3, cursor: "pointer", accentColor: (p: RBPaySetupTheme) => p.secondary },
  checkboxLabel: { fontSize: 13, color: C.textBody, lineHeight: 1.75, cursor: "pointer" },
  successBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    padding: "14px 16px",
    background: C.successBg,
    border: `1px solid ${C.successBorder}`,
    borderRadius: 8,
  },
  successText: { fontSize: 13, color: C.successText, lineHeight: 1.55, margin: 0 },
  sectionError: { color: C.errorText, fontSize: 13, marginTop: 14 },
  actionRow: { display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "12px", marginTop: 4 },
  cancelButton: {
    padding: "11px 24px",
    background: "transparent",
    color: "#6B5F8A",
    border: `1.5px solid #CCC5E0`,
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer",
  },
  saveButton: {
    padding: "11px 32px",
    background: (p: RBPaySetupTheme) => p.primary,
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    "&:disabled": { opacity: 0.55, cursor: "not-allowed" },
  },
  emptyState: { padding: 32, color: C.textMuted },
}));

interface LocationState {
  selectedLoc?: any;
  newPharmacy?: boolean;
}

const statusPillStyle = (status: string): React.CSSProperties => {
  const upper = String(status || "").toUpperCase();
  if (upper === RBPayrollStatus.ACTIVE) {
    return { color: C.successText, background: C.successBg, border: `1px solid ${C.successBorder}` };
  }
  if (upper === RBPayrollStatus.PENDING_REVIEW) {
    return { color: C.warnText, background: C.warnBg, border: `1px solid ${C.warnBorder}` };
  }
  if (upper === RBPayrollStatus.ON_HOLD || upper === RBPayrollStatus.REJECTED) {
    return { color: C.errorText, background: "#FEF2F2", border: "1px solid #FECACA" };
  }
  return { color: C.textLabel, background: C.summaryBg, border: `1px solid ${C.summaryBorder}` };
};

const useSummaryRowStyles = makeStyles({
  row: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 16px", gap: 16 },
  key: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: C.textLabel,
  },
  value: { fontSize: 13.5, fontWeight: 500, color: C.textDark, textAlign: "right", overflowWrap: "anywhere" },
});

const SummaryRow = ({ label, value, divider }: { label: string; value: React.ReactNode; divider: boolean }) => {
  const classes = useSummaryRowStyles();
  return (
    <div className={classes.row} style={divider ? { borderBottom: `1px solid ${C.summaryBorder}` } : undefined}>
      <span className={classes.key}>{label}</span>
      <span className={classes.value}>{value}</span>
    </div>
  );
};

export const RBPayrollOwnerSetupPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { selectedLoc } = location.state || {};
  const { primaryColor, secondaryColor } = useContext(RBContext);
  const brandTheme = useMemo(() => buildRBPaySetupTheme(primaryColor, secondaryColor), [primaryColor, secondaryColor]);
  const classes = useStyles(brandTheme);

  const [activeLocation, setActiveLocation] = useState<any>(selectedLoc || null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);

  const [setupSelectionMode, setSetupSelectionMode] = useState<RBPayrollLocationCreationModeValue>(
    RBPayrollLocationCreationMode.NEW
  );
  const [setupSelectionLocationId, setSetupSelectionLocationId] = useState("");
  const [setupSelectionError, setSetupSelectionError] = useState("");
  const [hasHandledSetupSelection, setHasHandledSetupSelection] = useState(false);
  const [isPreparingSetup, setIsPreparingSetup] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [successModal, setSuccessModal] = useState<null | "first" | "edit" | "copied">(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);

  const checklistRef = useRef<HTMLDivElement | null>(null);
  const bankingSectionRef = useRef<HTMLDivElement | null>(null);
  const authSectionRef = useRef<HTMLDivElement | null>(null);

  // Ensure
  const { accountInformation, fetchPharmacy } = useContext(AccountInfoContext);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        if (!accountInformation) return;
        if (!isMounted) return;

        if (selectedLoc?.id) {
          setActiveLocation(selectedLoc);
          return;
        }

        const defaultLocationId = accountInformation?.owner?.defaultPharmacyId || accountInformation?.pharmacies?.[0]?.id;
        if (!defaultLocationId) {
          setActiveLocation(null);
          return;
        }

        const nextLocation = await fetchPharmacy(defaultLocationId, { preferCache: true });
        if (isMounted) setActiveLocation(nextLocation);
      } finally {
        if (isMounted) setIsBootstrapping(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [selectedLoc]);

  const settings = useRBPayrollOwnerSettings({ account: accountInformation, location: activeLocation });
  const {
    mode,
    status,
    draftState,
    isEditing,
    isDirty,
    isSaving,
    isSaveDisabled,
    documentUploadError,
    setDocumentUploadError,
    statementPortalUrl,
    contactEmail,
    steps,
    activeStepKey,
    isSetupComplete,
    paymentOptions,
    selectRBPayroll,
  } = settings;

  const activeLocationId = activeLocation?.id;
  const shouldShowSetupSelection = useMemo(() => {
    // Hold the selection screen while the copy-success modal is up so the page
    // underneath doesn't flip to the pending setup view before we redirect.
    if (successModal === "copied") return true;
    if (!settings.setupState || hasHandledSetupSelection) return false;
    return settings.setupState.approval.status === RBPayrollStatus.NOT_STARTED && settings.duplicateSources.length > 0;
  }, [successModal, hasHandledSetupSelection, settings.duplicateSources.length, settings.setupState]);

  useEffect(() => {
    setHasHandledSetupSelection(false);
    setSetupSelectionError("");
  }, [activeLocationId]);

  useEffect(() => {
    if (!paymentOptions.length) return;
    if (!paymentOptions.some((option) => option.selected)) selectRBPayroll();
  }, [paymentOptions, selectRBPayroll]);

  const isFormMode = mode === "setup" || isEditing;

  // State-aware checklist notice: editing/first-time setup shows an in-progress
  // "Save Setup" prompt; pending reads as "submitted"; rejected asks to resubmit;
  // active reads as "complete".
  const setupNotice = useMemo<RBPayStepNotice>(() => {
    if (isFormMode) {
      return {
        tone: "warn",
        message: isEditing ? RB_PAYROLL_SETUP_NOTES.editing : RB_PAYROLL_SETUP_NOTES.incomplete,
      };
    }
    if (status === RBPayrollStatus.PENDING_REVIEW) {
      return { tone: "warn", message: RB_PAYROLL_SETUP_NOTES.submitted };
    }
    if (status === RBPayrollStatus.ON_HOLD) {
      return { tone: "warn", message: RB_PAYROLL_SETUP_NOTES.onHold };
    }
    if (status === RBPayrollStatus.REJECTED) {
      return { tone: "warn", message: RB_PAYROLL_SETUP_NOTES.rejected };
    }
    return { tone: "success", message: RB_PAYROLL_SETUP_NOTES.complete };
  }, [isFormMode, isEditing, status]);

  const openStatements = () => {
    if (statementPortalUrl) window.open(statementPortalUrl, "_blank", "noopener,noreferrer");
  };

  const renderFieldError = (message?: string) =>
    message ? <div className={classes.fieldError}>{message}</div> : null;

  const sectionRefs: Record<string, React.RefObject<HTMLDivElement>> = {
    banking: bankingSectionRef,
    authorization: authSectionRef,
  };

  const scrollToSection = (key: string) => {
    const element = sectionRefs[key]?.current;
    if (!element) return;
    const checklistHeight = checklistRef.current?.offsetHeight || 0;
    const targetY = element.getBoundingClientRect().top + window.scrollY - TopAppBarHeight - checklistHeight - 16;
    window.scrollTo({ top: Math.max(targetY, 0), behavior: "smooth" });
  };

  const handleSave = async () => {
    const wasFirstTime = mode === "setup";
    const firstInvalid = settings.getFirstInvalidSection();
    const result = await settings.save();

    if (result === "saved") {
      setSuccessModal(wasFirstTime ? "first" : "edit");
    } else if (result === "invalid") {
      if (firstInvalid) window.setTimeout(() => scrollToSection(firstInvalid), 60);
    } else if (result === "error") {
      setErrorModalOpen(true);
    }
  };

  const handleCancel = () => {
    if (mode === "setup") {
      history.push(RoutePathConstant.StripePayment);
      return;
    }
    settings.cancelEdit();
  };

  const closeSuccessAndRedirect = () => {
    setSuccessModal(null);
    history.push(RoutePathConstant.StripePayment);
  };

  // ── Document upload (dropzone, no synthetic-event hazard) ─────────────
  const handleDropFiles = async (files: File[]) => {
    const file = files && files[0];
    if (!file) return;

    if (!RB_PAYROLL_ALLOWED_DOCUMENT_TYPES.includes(file.type)) {
      setDocumentUploadError("Please upload a PDF, PNG, JPG, or JPEG file.");
      settings.markFieldTouched("directDeposit.document");
      return;
    }
    if (file.size > RB_PAYROLL_MAX_DOCUMENT_SIZE) {
      setDocumentUploadError("Document must be 10 MB or less.");
      settings.markFieldTouched("directDeposit.document");
      return;
    }

    setDocumentUploadError("");
    setIsUploading(true);
    try {
      await settings.uploadDocument(file);
    } catch (error) {
      const err: any = error;
      setDocumentUploadError(err?.message || "Unable to upload document. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDropRejected = () => {
    setDocumentUploadError("Please upload a PDF, PNG, or JPG file under 10 MB.");
    settings.markFieldTouched("directDeposit.document");
  };

  const appbarActions = (
    <div className={classes.appbarActions}>
      <span className={classes.statusPill} style={statusPillStyle(status)}>
        {getRBPayrollStatusLabel(status)}
      </span>
      <div className={classes.appbarStatements}>
        <button type="button" className={classes.statementsButton} onClick={openStatements} disabled={!statementPortalUrl}>
          View Statements
        </button>
        <Tooltip title={RB_PAYROLL_STATEMENT_ACCESS_NOTE} arrow placement="bottom-end">
          <InfoOutlined className={classes.infoIcon} aria-label={RB_PAYROLL_STATEMENT_ACCESS_NOTE} />
        </Tooltip>
      </div>
    </div>
  );

  if (isBootstrapping || settings.isPageLoading) {
    return (
      <>
        <RBAppbar deeperLevel barName="RB Pay Setup" />
        <LoadingIndicator />
      </>
    );
  }

  if (!accountInformation || !activeLocation || !settings.setupState || !draftState) {
    return (
      <>
        <RBAppbar deeperLevel barName="RB Pay Setup" />
        <div className={classes.emptyState}>
          {renderRBPayText("No location is available for RB Pay setup.")}
          <div style={{ marginTop: 16 }}>
            <RBButton
              text="Back to Payment Settings"
              buttonBackgroundColor={brandTheme.primary}
              onClick={() => history.push(RoutePathConstant.StripePayment)}
            />
          </div>
        </div>
      </>
    );
  }

  // ── Setup Selection (copy vs new) ────────────────────────────────────
  if (shouldShowSetupSelection) {
    const handleSelectionContinue = async () => {
      if (setupSelectionMode === RBPayrollLocationCreationMode.NEW) {
        setSetupSelectionError("");
        setHasHandledSetupSelection(true);
        return;
      }

      if (!setupSelectionLocationId) {
        setSetupSelectionError("Select an existing location before continuing.");
        return;
      }
      setSetupSelectionError("");

      // Copy the selected location's setup directly — no review/re-authorize
      // step. On success, show the confirmation modal and return the owner to
      // Payment Settings; on failure, stay here and surface the inline error.
      setIsPreparingSetup(true);
      try {
        const result = await settings.submitDuplicate(setupSelectionLocationId);
        if (result === "saved") {
          // Keep the selection screen frozen behind the modal (see
          // shouldShowSetupSelection) instead of revealing the pending setup page.
          // Deliberately NOT setHasHandledSetupSelection here; OK redirects.
          setSuccessModal("copied");
        }
      } finally {
        setIsPreparingSetup(false);
      }
    };

    return (
      <>
        <RBAppbar deeperLevel barName="RB Pay Setup" />
        <div className={classes.root}>
          <div className={classes.contentNarrow}>
            <RBPayrollSetupSelection
              mode={setupSelectionMode}
              selectedLocationId={setupSelectionLocationId}
              sources={settings.duplicateSources}
              isSubmitting={isPreparingSetup}
              isDuplicateLoading={settings.isDuplicating}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              errorMessage={setupSelectionError || settings.errorMessage}
              onModeChange={(nextMode) => {
                setSetupSelectionMode(nextMode);
                if (nextMode !== RBPayrollLocationCreationMode.COPY) setSetupSelectionLocationId("");
                setSetupSelectionError("");
              }}
              onLocationChange={(locationId) => {
                setSetupSelectionMode(RBPayrollLocationCreationMode.COPY);
                setSetupSelectionLocationId(locationId);
                setSetupSelectionError("");
              }}
              onContinue={handleSelectionContinue}
            />
          </div>
        </div>
        {/* Copy-success confirmation lives here too: shouldShowSetupSelection is
            held true while the modal is up, so this early return owns the modal
            until OK redirects to Payment Settings. */}
        <RBAlert
          show={successModal === "copied"}
          alertTitle={RB_PAYROLL_COPY_SUBMIT_TITLE}
          alertMessage={RB_PAYROLL_COPY_SUBMIT_MESSAGE}
          buttons={[
            <RBButton
              key="ok"
              text="OK"
              buttonBackgroundColor={brandTheme.primary}
              onClick={closeSuccessAndRedirect}
            />,
          ]}
        />
      </>
    );
  }

  const document = draftState.directDeposit.document;

  const renderBankingBody = () => {
    if (!isFormMode) {
      return (
        <div className={classes.summaryBox}>
          <SummaryRow label="Institution #" value={draftState.directDeposit.institutionNumber || "—"} divider />
          <SummaryRow label="Transit #" value={draftState.directDeposit.transitNumber || "—"} divider />
          <SummaryRow label="Account #" value={maskAccountNumber(draftState.directDeposit.accountNumber) || "—"} divider />
          <SummaryRow
            label="Document"
            value={
              document ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: C.successIcon }}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <circle cx="7" cy="7" r="6.5" fill="#DCFCE7" />
                    <path d="M4 7l2.5 2.5 4-4" stroke={C.successIcon} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Uploaded
                </span>
              ) : (
                "—"
              )
            }
            divider={false}
          />
        </div>
      );
    }

    return (
      <>
        {status === RBPayrollStatus.NOT_STARTED ? (
          <div className={classes.noticeBox} style={{ background: C.warnBg, border: `1px solid ${C.warnBorder}` }}>
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
              <circle cx="8" cy="8" r="6.5" stroke={C.warnIcon} strokeWidth="1.4" />
              <line x1="8" y1="4.5" x2="8" y2="9" stroke={C.warnIcon} strokeWidth="1.4" strokeLinecap="round" />
              <circle cx="8" cy="11.2" r="0.8" fill={C.warnIcon} />
            </svg>
            <div>
              <p className={classes.noticeTitle} style={{ color: C.warnText }}>
                {renderRBPayText(RB_PAYROLL_STATUS_COPY[RBPayrollStatus.NOT_STARTED].title)}
              </p>
              <p className={classes.noticeText} style={{ color: "#A05215" }}>
                {RB_PAYROLL_STATUS_COPY[RBPayrollStatus.NOT_STARTED].description}
              </p>
            </div>
          </div>
        ) : null}

        {status === RBPayrollStatus.REJECTED && settings.setupState?.approval.rejectionReason ? (
          <div className={classes.noticeBox} style={{ background: "#FEF2F2", border: "1px solid #FECACA" }}>
            <div>
              <p className={classes.noticeTitle} style={{ color: C.errorText }}>
                {renderRBPayText(RB_PAYROLL_STATUS_COPY[RBPayrollStatus.REJECTED].title)}
              </p>
              <p className={classes.noticeText} style={{ color: "#B42318" }}>
                {settings.setupState?.approval.rejectionReason}
              </p>
            </div>
          </div>
        ) : null}

        <div className={classes.twoCol}>
          <div>
            <label className={classes.inputLabel}>Institution Number</label>
            <input
              className={classes.input}
              value={draftState.directDeposit.institutionNumber}
              inputMode="numeric"
              maxLength={3}
              onChange={(event) => settings.updateDraft("directDeposit.institutionNumber", event.target.value)}
              onBlur={() => settings.markFieldTouched("directDeposit.institutionNumber")}
            />
            {renderFieldError(settings.getFieldError("directDeposit.institutionNumber"))}
          </div>
          <div>
            <label className={classes.inputLabel}>Transit Number</label>
            <input
              className={classes.input}
              value={draftState.directDeposit.transitNumber}
              inputMode="numeric"
              maxLength={5}
              onChange={(event) => settings.updateDraft("directDeposit.transitNumber", event.target.value)}
              onBlur={() => settings.markFieldTouched("directDeposit.transitNumber")}
            />
            {renderFieldError(settings.getFieldError("directDeposit.transitNumber"))}
          </div>
        </div>

        <div style={{ marginBottom: 18 }}>
          <label className={classes.inputLabel}>Account Number</label>
          <input
            className={classes.input}
            style={{ maxWidth: 340 }}
            value={draftState.directDeposit.accountNumber}
            inputMode="numeric"
            maxLength={12}
            onChange={(event) => settings.updateDraft("directDeposit.accountNumber", event.target.value)}
            onBlur={() => settings.markFieldTouched("directDeposit.accountNumber")}
          />
          {renderFieldError(settings.getFieldError("directDeposit.accountNumber"))}
        </div>

        <div className={classes.uploadLabelRow}>
          <label className={classes.inputLabel} style={{ marginBottom: 0 }}>
            Upload Document
          </label>
        </div>

        {document ? (
          <>
            <div className={classes.dropzoneUploaded}>
              {document.previewUrl && isImageDocument(document.type) ? (
                <img src={document.previewUrl} alt={document.name} className={classes.filePreviewImg} />
              ) : (
                <svg width="38" height="38" viewBox="0 0 36 36" fill="none">
                  <rect width="36" height="36" rx="8" fill="#EDE8F7" />
                  <path d="M18 11v11M18 11l-4 4.5M18 11l4 4.5" stroke="#9B8FC0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M10 25h16" stroke="#C8C0DC" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              )}
              <p className={classes.dropzonePrimary}>Document uploaded</p>
            </div>
            <div className={classes.fileActions}>
              <button type="button" className={classes.linkButton} onClick={() => setPreviewOpen(true)} disabled={!document.previewUrl}>
                Preview
              </button>
              <button type="button" className={classes.linkButton} onClick={settings.removeDocument}>
                Remove
              </button>
            </div>
          </>
        ) : (
          <DropzoneArea
            acceptedFiles={RB_PAYROLL_ALLOWED_DOCUMENT_TYPES}
            filesLimit={1}
            maxFileSize={RB_PAYROLL_MAX_DOCUMENT_SIZE}
            showAlerts={false}
            showPreviewsInDropzone={false}
            dropzoneClass={classes.dropzoneEmpty}
            dropzoneText={isUploading ? "Uploading…" : "No document uploaded"}
            onChange={handleDropFiles}
            onDropRejected={handleDropRejected}
          />
        )}
        <p className={classes.uploadHelp}>{RB_PAYROLL_DOCUMENT_HELPER_TEXT}</p>
        {renderFieldError(documentUploadError || settings.getFieldError("directDeposit.document"))}
      </>
    );
  };

  const renderAuthorizationBody = () => {
    if (!isFormMode) {
      return (
        <div className={classes.successBox}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
            <circle cx="8" cy="8" r="7" fill={C.successIcon} />
            <path d="M5 8l2.5 2.5 4-4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <p className={classes.successText}>{RB_PAYROLL_AUTHORIZED_SUMMARY}</p>
        </div>
      );
    }

    return (
      <>
        <div className={classes.checkboxBox}>
          <input
            type="checkbox"
            id="owner-auth"
            className={classes.checkbox}
            checked={draftState.directDeposit.authorizationAccepted}
            onChange={(event) => settings.updateDraft("directDeposit.authorizationAccepted", event.target.checked)}
          />
          <label htmlFor="owner-auth" className={classes.checkboxLabel}>
            {RB_PAYROLL_AUTHORIZATION_TEXT}
          </label>
        </div>
        {renderFieldError(settings.getFieldError("directDeposit.authorizationAccepted"))}
      </>
    );
  };

  const contactMailto = `mailto:${contactEmail}?subject=${encodeURIComponent("Questions about RB Pay")}`;

  return (
    <>
      <RBAppbar deeperLevel barName="RB Pay Setup" barChildren={appbarActions} />
      <div className={classes.root}>
        <div className={classes.content}>
          <div className={classes.stickyChecklist} ref={checklistRef}>
            <RBPayStepProgress
              steps={steps}
              activeStepKey={activeStepKey}
              isSetupComplete={isSetupComplete}
              notice={setupNotice}
              theme={brandTheme}
              onStepClick={scrollToSection}
            />
          </div>

          <div className={classes.banner}>
            <div className={classes.bannerHead}>
              <div className={classes.bannerIcon}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <circle cx="7" cy="4.5" r="1" fill="white" />
                  <rect x="6" y="7" width="2" height="4.5" rx="1" fill="white" />
                  <circle cx="7" cy="7" r="6" stroke="rgba(255,255,255,0.4)" strokeWidth="1.2" />
                </svg>
              </div>
              <h2 className={classes.bannerTitle}>{renderRBPayText("RB Pay Information")}</h2>
            </div>
            <p className={classes.bannerSubTitle}>{renderRBPayText(RB_PAYROLL_ABOUT_TITLE)}</p>
            <p className={classes.bannerIntro}>{RB_PAYROLL_ABOUT_INTRO}</p>
            <div className={classes.bannerBullets}>
              {RB_PAYROLL_ABOUT_BULLETS.map((bullet) => (
                <div className={classes.bannerBulletRow} key={bullet}>
                  <div className={classes.bannerBulletDot} />
                  <p className={classes.bannerBulletText}>{bullet}</p>
                </div>
              ))}
            </div>
            <div className={classes.bannerFooter}>
              <p className={classes.bannerMeta}>
                Questions? Contact us{" "}
                <a className={classes.bannerLink} href={contactMailto}>
                  {contactEmail}
                </a>
              </p>
            </div>
          </div>

          <div ref={bankingSectionRef}>
            <RBPaySetupSection
              stepNumber="01"
              title={renderRBPayText("Direct Deposit Information – To Send Payments")}
              description={renderRBPayText("Enter your banking details to process shift payments through RB Pay.")}
              theme={brandTheme}
            >
              {renderBankingBody()}
            </RBPaySetupSection>
          </div>

          <div ref={authSectionRef}>
            <RBPaySetupSection
              stepNumber="02"
              title="Authorization"
              description={renderRBPayText("Review and authorize your RB Pay payment setup.")}
              theme={brandTheme}
            >
              {renderAuthorizationBody()}
            </RBPaySetupSection>
          </div>

          {settings.errorMessage ? <div className={classes.sectionError}>{settings.errorMessage}</div> : null}

          {isFormMode ? (
            <div className={classes.actionRow}>
              <button type="button" className={classes.cancelButton} onClick={handleCancel} disabled={isSaving}>
                Cancel
              </button>
              <Tooltip title={isEditing && !isDirty ? RB_PAYROLL_NO_CHANGES_TOOLTIP : ""} arrow>
                <span style={{ display: "inline-flex" }}>
                  <button type="button" className={classes.saveButton} onClick={handleSave} disabled={isSaveDisabled}>
                    {isSaving ? "Saving…" : "Save Setup"}
                  </button>
                </span>
              </Tooltip>
            </div>
          ) : (
            <div className={classes.actionRow}>
              <button type="button" className={classes.saveButton} onClick={settings.startEdit}>
                Edit
              </button>
            </div>
          )}
        </div>
      </div>

      <RBPayDocumentPreviewModal
        open={previewOpen}
        previewUrl={document?.previewUrl || ""}
        fileType={document?.type || ""}
        fileName={document?.name || "Uploaded Document"}
        onClose={() => setPreviewOpen(false)}
      />

      <RBAlert
        show={successModal !== null}
        alertTitle={
          successModal === "copied"
            ? RB_PAYROLL_COPY_SUBMIT_TITLE
            : successModal === "edit"
            ? RB_PAYROLL_EDIT_SUBMIT_TITLE
            : RB_PAYROLL_FIRST_SUBMIT_TITLE
        }
        alertMessage={
          successModal === "copied"
            ? RB_PAYROLL_COPY_SUBMIT_MESSAGE
            : successModal === "edit"
            ? RB_PAYROLL_EDIT_SUBMIT_MESSAGE
            : RB_PAYROLL_FIRST_SUBMIT_MESSAGE
        }
        buttons={[
          <RBButton key="ok" text="OK" buttonBackgroundColor={brandTheme.primary} onClick={closeSuccessAndRedirect} />,
        ]}
      />

      <RBAlert
        show={errorModalOpen}
        alertTitle={RB_PAYROLL_SUBMIT_ERROR_TITLE}
        alertMessage={RB_PAYROLL_SUBMIT_ERROR_MESSAGE}
        buttons={[
          <RBButton key="ok" text="OK" buttonBackgroundColor={brandTheme.primary} onClick={() => setErrorModalOpen(false)} />,
        ]}
      />
    </>
  );
};
